import 'rc-slider/assets/index.css';
import { css } from '@emotion/react';
import Slider from 'rc-slider';
import {
  cloneElement, type FC, type ReactElement, type ReactNode, useCallback, useMemo,
} from 'react';
import { type Theme } from '~/_shared/themes/theme.model';
import {
  HANDLE_HOVER_SCALE, RAIL_HOVERED_HANDLE_CLASS,
} from '../slider.constants';
import { useRailHoveredHandle } from '../useRailHoveredHandle.hook';

const MIN = 0;
const MAX = 100;
const STEP = 1;
const RAIL_HEIGHT = 6;
const HANDLE_SIZE = 16;

const wrapperStyle = css({
  padding: '4px 0',
});

const sliderStyle = css({
  '.rc-slider-handle': {
    '&, &.rc-slider-handle-dragging': {
      width: HANDLE_SIZE,
      height: HANDLE_SIZE,
      marginTop: -(HANDLE_SIZE - RAIL_HEIGHT) / 2,
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      opacity: 1,
    },
    [`&:hover > div, &.${RAIL_HOVERED_HANDLE_CLASS} > div, &.rc-slider-handle-dragging > div`]: {
      transform: `scale(${HANDLE_HOVER_SCALE})`,
    },
  },
});

const handleSwatchStyle = css({
  transition: 'transform .15s',
  width: HANDLE_SIZE,
  height: HANDLE_SIZE,
  borderRadius: '50%',
  border: '2px solid #fff',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.35)',
  boxSizing: 'border-box',
});

const labelsRowStyle = (theme: Theme) => css({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: 6,
  fontSize: 11,
  color: theme.textColors.secondary,
});

type GradientSliderProps = Readonly<{
  className?: string;
  /** Ordered low to high; each color maps to a handle. Hex with or without a leading `#`. */
  colors: ReadonlyArray<string>;
  /** Positions (0–1) of the inner (movable) handles, ordered low to high. Length is `colors.length - 2`. */
  value: ReadonlyArray<number>;
  minLabel?: ReactNode;
  maxLabel?: ReactNode;
  onChange: (value: ReadonlyArray<number>) => void;
}>;

const normalizeColor = (color: string): string => (color.startsWith('#') ? color : `#${color}`);

const buildGradient = (colors: ReadonlyArray<string>, positions: ReadonlyArray<number>): string => {
  const parts = colors.map((color, index) => `${normalizeColor(color)} ${positions[index] ?? 0}%`);
  return `linear-gradient(to right, ${parts.join(', ')})`;
};

export const GradientSliderComponent: FC<GradientSliderProps> = ({
  className, colors, value, minLabel, maxLabel, onChange,
}) => {
  const positions = useMemo(() => [MIN, ...value.map(v => v * MAX), MAX], [value]);
  const gradient = useMemo(() => buildGradient(colors, positions), [colors, positions]);
  const lastIndex = colors.length - 1;

  const changeHandler = useCallback((values: number | number[]) => {
    const next = Array.isArray(values) ? values : [values];
    // The endpoints stay fixed - only propagate the inner handle positions (as fractions).
    onChange(next.slice(1, -1).map(position => position / MAX));
  }, [onChange]);

  // Only the inner handles are movable
  const railHoverCandidates = useMemo(() =>
    positions
      .map((position, index) => ({ index, value: position }))
      .slice(1, -1),
  [positions],
  );

  const { hoveredIndex, onMouseMove, onMouseLeave, classNames } = useRailHoveredHandle({
    candidates: railHoverCandidates,
    min: MIN,
    max: MAX,
    enabled: railHoverCandidates.length > 0,
  });

  const handleRender = useCallback((
    node: ReactElement<React.HTMLAttributes<HTMLDivElement>>,
    handleProps: { index: number },
  ): ReactElement => {
    const isEndHandle = handleProps.index === 0 || handleProps.index === lastIndex;
    return cloneElement(node, {
      className: handleProps.index === hoveredIndex
        ? `${node.props.className ?? ''} ${RAIL_HOVERED_HANDLE_CLASS}`
        : node.props.className,
      style: {
        ...node.props.style,
        ...(isEndHandle ? { pointerEvents: 'none', cursor: 'default' } : {}),
      },
    }, (
      <div
        css={handleSwatchStyle}
        style={{ backgroundColor: normalizeColor(colors[handleProps.index] ?? '') }}
      />
    ));
  }, [colors, lastIndex, hoveredIndex]);

  return (
    <div
      className={className}
      css={wrapperStyle}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <Slider
        classNames={classNames}
        range
        allowCross={false}
        css={sliderStyle}
        min={MIN}
        max={MAX}
        step={STEP}
        value={positions}
        onChange={changeHandler}
        handleRender={handleRender}
        styles={{
          rail: { background: gradient, height: RAIL_HEIGHT, borderRadius: RAIL_HEIGHT / 2 },
          track: { background: 'transparent', height: RAIL_HEIGHT },
        }}
      />

      {(minLabel !== undefined || maxLabel !== undefined) && (
        <div css={labelsRowStyle}>
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
};
